/**
 * src/utils/generateOrderPDF.ts
 * Generates a professional order confirmation PDF using jsPDF.
 * Compact layout — fits Note, T&C, and Footer on a single A4 page.
 */
import { jsPDF } from "jspdf";

export interface OrderPDFData {
  referenceNo: string;
  orderId?: string;
  deviceName: string;
  brandName: string;
  payoutAmount: number;
  customerName: string;
  customerPhone: string;
  pickupDate: string;
  timeSlot: string;
  address: string;
  productImageUrl?: string;
}

// ── Color Palette ──────────────────────────────────────────────────
const BRAND       = [29, 158, 117] as const;
const BRAND_DARK  = [15, 110, 86]  as const;
const TXT         = [30, 41, 59]   as const;
const TXT2        = [100, 116, 139] as const;
const TXT3        = [148, 163, 184] as const;
const BORDER      = [226, 232, 240] as const;
const SURF        = [248, 250, 252] as const;
const W           = [255, 255, 255] as const;
const GREEN_BG    = [220, 252, 231] as const;
const AMBER_BG    = [255, 251, 235] as const;
const AMBER_BORDER= [251, 191, 36]  as const;

type RGB = readonly [number, number, number];

function rrect(d: jsPDF, x: number, y: number, w: number, h: number, r: number, fill: RGB, stroke?: RGB) {
  d.setFillColor(...fill);
  if (stroke) { d.setDrawColor(...stroke); d.setLineWidth(0.4); d.roundedRect(x, y, w, h, r, r, "FD"); }
  else d.roundedRect(x, y, w, h, r, r, "F");
}

function hline(d: jsPDF, x: number, y: number, w: number, c: RGB = BORDER) {
  d.setDrawColor(...c); d.setLineWidth(0.3); d.line(x, y, x + w, y);
}

async function fetchImageAsBase64(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    // Fallback: use Image + Canvas
    try {
      return await new Promise<string | null>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(null); return; }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });
    } catch { return null; }
  }
}

function getImageFormat(dataUrl: string): "JPEG" | "PNG" | "GIF" | "WEBP" {
  if (dataUrl.includes("image/png")) return "PNG";
  if (dataUrl.includes("image/gif")) return "GIF";
  if (dataUrl.includes("image/webp")) return "WEBP";
  return "JPEG";
}

export async function generateOrderPDF(data: OrderPDFData) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pw = 210, ph = 297, mx = 18;
  const cw = pw - mx * 2;
  let y = 0;

  let imageBase64: string | null = null;
  console.log('[PDF] productImageUrl:', data.productImageUrl);
  if (data.productImageUrl) {
    imageBase64 = await fetchImageAsBase64(data.productImageUrl);
    console.log('[PDF] imageBase64 loaded:', !!imageBase64, imageBase64?.substring(0, 60));
  }

  // ══════════════════════════════════════════════════════════════════
  // HEADER — 38mm tall (compact)
  // ══════════════════════════════════════════════════════════════════
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pw, 36, "F");
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 33, pw, 3, "F");

  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...W);
  doc.text("True Value Mobiles", mx, 16);

  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(220, 252, 231);
  doc.text("Sell Your Device, Get Instant Cash", mx, 23);

  // Badge
  const badge = "ORDER CONFIRMATION";
  doc.setFont("helvetica", "bold"); doc.setFontSize(8);
  const bw = doc.getTextWidth(badge) + 12;
  rrect(doc, pw - mx - bw, 9, bw, 8, 2, W as unknown as RGB);
  doc.setTextColor(...BRAND);
  doc.text(badge, pw - mx - bw / 2, 14.5, { align: "center" });

  // Date
  const now = new Date();
  const ds = `${now.getDate().toString().padStart(2,'0')}/${(now.getMonth()+1).toString().padStart(2,'0')}/${now.getFullYear()} ${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}`;
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(200, 240, 225);
  doc.text(`Generated: ${ds}`, pw - mx, 28, { align: "right" });

  y = 42;

  // ══════════════════════════════════════════════════════════════════
  // REFERENCE + STATUS — 20mm
  // ══════════════════════════════════════════════════════════════════
  rrect(doc, mx, y, cw, 20, 3, GREEN_BG, [186, 230, 203] as unknown as RGB);

  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...TXT2);
  doc.text("Reference Number", mx + 8, y + 7);

  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...BRAND);
  doc.text(data.referenceNo, mx + 8, y + 16);

  if (data.orderId) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...TXT2);
    doc.text(`Order ID: ${data.orderId}`, pw - mx - 8, y + 16, { align: "right" });
  }

  // Status badge
  doc.setFont("helvetica", "bold"); doc.setFontSize(7);
  const st = "CONFIRMED", sw = doc.getTextWidth(st) + 8;
  rrect(doc, pw - mx - sw - 4, y + 3, sw, 7, 2, BRAND as unknown as RGB);
  doc.setTextColor(...W);
  doc.text(st, pw - mx - sw / 2 - 4, y + 8, { align: "center" });

  y += 25;

  // ══════════════════════════════════════════════════════════════════
  // PAYOUT — 22mm
  // ══════════════════════════════════════════════════════════════════
  rrect(doc, mx, y, cw, 22, 3, W as unknown as RGB, BRAND as unknown as RGB);

  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...TXT2);
  doc.text("You will receive", mx + 8, y + 8);

  doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(...BRAND);
  doc.text(`Rs. ${data.payoutAmount.toLocaleString("en-IN")}`, mx + 8, y + 18);

  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...TXT3);
  doc.text("Paid via Cash / NEFT / UPI after inspection", pw - mx - 8, y + 13, { align: "right" });

  y += 27;

  // ══════════════════════════════════════════════════════════════════
  // ORDER DETAILS TABLE — compact rows
  // ══════════════════════════════════════════════════════════════════
  const sections: { title: string; rows: { l: string; v: string }[] }[] = [
    {
      title: "Device Details",
      rows: [
        { l: "Device", v: `${data.brandName} ${data.deviceName}`.trim() || "—" },
        { l: "Quoted Price", v: `Rs. ${data.payoutAmount.toLocaleString("en-IN")}` },
      ],
    },
    {
      title: "Customer Details",
      rows: [
        { l: "Name", v: data.customerName || "—" },
        { l: "Phone", v: data.customerPhone ? `+91 ${data.customerPhone}` : "—" },
      ],
    },
    {
      title: "Pickup Schedule",
      rows: [
        { l: "Date", v: data.pickupDate || "—" },
        { l: "Time Slot", v: data.timeSlot || "—" },
        { l: "Address", v: data.address || "—" },
      ],
    },
  ];

  sections.forEach((sec, secIdx) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...TXT);
    doc.text(sec.title, mx, y);
    y += 2;
    hline(doc, mx, y, cw, BRAND as unknown as RGB);
    y += 5;

    const deviceImageWidth = 30;
    const deviceImageHeight = 30;
    const textAreaWidth = (secIdx === 0 && imageBase64) ? cw - deviceImageWidth - 10 : cw;

    sec.rows.forEach(row => {
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...TXT2);
      doc.text(row.l, mx + 3, y);
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...TXT);

      if (row.l === "Address" && row.v.length > 55) {
        const lines = doc.splitTextToSize(row.v, textAreaWidth - 50);
        doc.text(lines, mx + 45, y);
        y += (lines.length - 1) * 4;
      } else {
        doc.text(row.v, mx + 45, y);
      }
      y += 6;
    });

    if (secIdx === 0 && imageBase64) {
      try {
        const imgX = mx + cw - deviceImageWidth;
        const imgY = y - sec.rows.length * 6 - 3;
        const fmt = getImageFormat(imageBase64);
        rrect(doc, imgX - 1, imgY - 1, deviceImageWidth + 2, deviceImageHeight + 2, 3, W as unknown as RGB, BORDER as unknown as RGB);
        doc.addImage(imageBase64, fmt, imgX, imgY, deviceImageWidth, deviceImageHeight);
      } catch { /* ignore if image fails */ }
    }

    y += 3;
  });

  // ══════════════════════════════════════════════════════════════════
  // WHAT HAPPENS NEXT — compact 42mm
  // ══════════════════════════════════════════════════════════════════
  const nextH = 42;
  rrect(doc, mx, y, cw, nextH, 3, SURF, BORDER as unknown as RGB);

  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...TXT);
  doc.text("What Happens Next?", mx + 8, y + 7);

  const steps = [
    "SMS confirmation with booking details",
    "Verified technician assigned to your order",
    "Technician arrives at your address on time",
    "Quick device inspection to verify condition",
    "Instant payment — Cash, NEFT, or UPI",
  ];

  let sy = y + 14;
  steps.forEach((txt, i) => {
    doc.setFillColor(...BRAND);
    doc.circle(mx + 12, sy - 0.8, 2.2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(5.5); doc.setTextColor(...W);
    doc.text(`${i + 1}`, mx + 12, sy, { align: "center" });

    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...TXT);
    doc.text(txt, mx + 19, sy);
    sy += 5.5;
  });

  y += nextH + 5;

  // ══════════════════════════════════════════════════════════════════
  // IMPORTANT NOTE — amber box
  // ══════════════════════════════════════════════════════════════════
  const noteH = 14;
  rrect(doc, mx, y, cw, noteH, 3, AMBER_BG as unknown as RGB, AMBER_BORDER as unknown as RGB);

  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(180, 83, 9);
  doc.text("IMPORTANT:", mx + 6, y + 5.5);

  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(120, 53, 15);
  doc.text("This is an Order Confirmation Receipt only. The final invoice will be provided after", mx + 30, y + 5.5);
  doc.text("the device is physically submitted and inspected by our technician.", mx + 30, y + 10);

  y += noteH + 5;

  // ══════════════════════════════════════════════════════════════════
  // TERMS & CONDITIONS — numbered inside card
  // ══════════════════════════════════════════════════════════════════
  const terms = [
    "Final price may vary after physical inspection of the device.",
    "Free cancellation available up to 2 hours before scheduled pickup.",
    "Valid government ID required at the time of pickup.",
    "Payment processed immediately after successful inspection.",
    "System-generated document — does not require a signature.",
  ];

  const tH = 8 + terms.length * 5 + 2;
  rrect(doc, mx, y, cw, tH, 3, SURF, BORDER as unknown as RGB);

  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...TXT);
  doc.text("Terms & Conditions", mx + 8, y + 6);

  let ty = y + 12;
  terms.forEach((t, i) => {
    doc.setFillColor(...BRAND);
    doc.circle(mx + 10, ty - 0.8, 1.5, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(4.5); doc.setTextColor(...W);
    doc.text(`${i + 1}`, mx + 10, ty, { align: "center" });

    doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...TXT2);
    doc.text(t, mx + 16, ty);
    ty += 5;
  });

  // ══════════════════════════════════════════════════════════════════
  // FOOTER — branded dark bar
  // ══════════════════════════════════════════════════════════════════
  const fy = ph - 14;
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, fy, pw, 14, "F");

  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...W);
  doc.text("True Value Mobiles", mx, fy + 5.5);

  doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(200, 230, 220);
  doc.text("support@truevaluemobiles.in  |  www.truevaluemobiles.in", mx, fy + 9.5);

  doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(200, 230, 220);
  doc.text(`Ref: ${data.referenceNo}  |  Page 1 of 1`, pw - mx, fy + 5.5, { align: "right" });
  doc.text(`Generated: ${ds}`, pw - mx, fy + 9.5, { align: "right" });

  // ── DOWNLOAD ──────────────────────────────────────────────────
  doc.save(`TrueValue_Order_${data.referenceNo}.pdf`);
}
